/**
 * HandPosePanel — floating SVG schematic for selecting finger bones.
 *
 * Shows both hands (palm-down, top view) with clickable joint circles
 * for the 30 finger bones (5 fingers x 3 joints x 2 hands). Uses the
 * DraggablePanel infrastructure for repositioning.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import { getBone, getRestPose } from "../mannequin/boneRegistry.ts";
import { FINGER_SPECS } from "../mannequin/fingerData.ts";
import { extractAxisAngle } from "../mannequin/quaternionMath.ts";
import { DraggablePanel } from "./DraggablePanel.tsx";
import { useDeferredUndo } from "./hooks/useDeferredUndo.ts";
import { SliderRow } from "./shared.tsx";
import { GLASS_PANEL } from "./styles.ts";
import {
  COLOR_HAND_JOINT_SELECTED,
  COLOR_HAND_LINE,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  COLOR_WARNING,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  RADIUS_PANEL,
  Z_POPOVER,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Finger layout data
// ---------------------------------------------------------------------------

interface FingerDef {
  name: string;
  joints: Array<{ x: number; y: number }>; // 3 joints, base to tip
}

// Y increases upward in the spec, but SVG Y increases downward.
// We define positions in SVG space (Y-down) with the wrist at the bottom.
// SVG viewBox: 0 0 120 200, wrist at (60, 180).

const LEFT_FINGERS: FingerDef[] = [
  {
    name: "pinky",
    joints: [
      { x: 20, y: 120 },
      { x: 20, y: 90 },
      { x: 20, y: 65 },
    ],
  },
  {
    name: "ring",
    joints: [
      { x: 40, y: 110 },
      { x: 40, y: 75 },
      { x: 40, y: 50 },
    ],
  },
  {
    name: "middle",
    joints: [
      { x: 60, y: 105 },
      { x: 60, y: 65 },
      { x: 60, y: 35 },
    ],
  },
  {
    name: "index",
    joints: [
      { x: 80, y: 110 },
      { x: 80, y: 75 },
      { x: 80, y: 50 },
    ],
  },
  {
    name: "thumb",
    joints: [
      { x: 95, y: 150 },
      { x: 105, y: 125 },
      { x: 110, y: 105 },
    ],
  },
];

// Mirror X coordinates for right hand
const RIGHT_FINGERS: FingerDef[] = LEFT_FINGERS.map((f) => ({
  name: f.name,
  joints: f.joints.map((j) => ({ x: 120 - j.x, y: j.y })),
}));

const WRIST = { x: 60, y: 180 };

// ---------------------------------------------------------------------------
// Bone naming helpers
// ---------------------------------------------------------------------------

// Three.js strips dots from glTF bone names:
// GLB: finger_index.L.1 → Three.js: finger_indexL1
function boneName(finger: string, side: "L" | "R", joint: number): string {
  return `finger_${finger}${side}${joint}`;
}

// ---------------------------------------------------------------------------
// Finger curl — realistic bend distribution per joint
// ---------------------------------------------------------------------------

const DEG = Math.PI / 180;

// Max flexion angles per joint at 100% curl, derived from anatomical specs.
// The ulnar gradient gives pinky more MCP flex (97°) than index (79°).
const CURL_ANGLES: Record<string, [number, number, number]> =
  Object.fromEntries(
    Object.entries(FINGER_SPECS).map(([name, s]) => [
      name,
      [s.mcpFlex * DEG, s.pipFlex * DEG, s.dipFlex * DEG] as [
        number,
        number,
        number,
      ],
    ]),
  );

// The local axis to curl around. Negative X = flexion for ARP finger bones.
const CURL_AXIS = new THREE.Vector3(-1, 0, 0);

// The thumb CMC (joint 1) curls along a compound axis (flexion + adduction)
// rather than pure -X like the other fingers.  Derived from a reference fist
// pose: delta ≈ Q(0.219, 0, 0.115, 0.969), axis ≈ (0.885, 0, ±0.465).
const THUMB_CMC_AXIS_L = new THREE.Vector3(0.885, 0, 0.465).normalize();
const THUMB_CMC_AXIS_R = new THREE.Vector3(0.885, 0, -0.465).normalize();

/**
 * Return the curl axis for a given finger joint.
 * All joints use -X except the thumb CMC which has a compound axis.
 */
function curlAxis(
  finger: string,
  side: "L" | "R",
  joint: number,
): THREE.Vector3 {
  if (finger === "thumb" && joint === 0) {
    return side === "L" ? THUMB_CMC_AXIS_L : THUMB_CMC_AXIS_R;
  }
  return CURL_AXIS;
}

/**
 * Compute curled quaternions for a finger's 3 joints.
 * Returns a Map of boneName → [x,y,z,w] ready for setBonePoses.
 */
function computeCurl(
  finger: string,
  side: "L" | "R",
  t: number, // 0–1 (slider value)
  objectId: string,
): Map<string, [number, number, number, number]> {
  const result = new Map<string, [number, number, number, number]>();
  const angles = CURL_ANGLES[finger];
  if (!angles) return result;

  for (let j = 0; j < 3; j++) {
    const name = boneName(finger, side, j + 1);
    const restQ = getRestPose(objectId, name);
    if (!restQ) continue;

    const rest = new THREE.Quaternion(restQ[0], restQ[1], restQ[2], restQ[3]);
    const axis = curlAxis(finger, side, j);
    const delta = new THREE.Quaternion().setFromAxisAngle(axis, angles[j] * t);
    const curled = rest.clone().multiply(delta);

    result.set(name, [curled.x, curled.y, curled.z, curled.w]);
  }

  return result;
}

/**
 * Read the current curl value (0–1) for a finger by inspecting stored bone
 * poses.  Averages the per-joint t values so the slider reflects the actual
 * pose even when the user switches between fingers.
 */
function readCurl(
  finger: string,
  side: "L" | "R",
  objectId: string,
  bonePoses: Map<string, [number, number, number, number]> | undefined,
): number {
  if (!bonePoses) return 0;
  const angles = CURL_ANGLES[finger];
  if (!angles) return 0;

  let sum = 0;
  let count = 0;
  for (let j = 0; j < 3; j++) {
    const name = boneName(finger, side, j + 1);
    const pose = bonePoses.get(name);
    const restQ = getRestPose(objectId, name);
    if (!pose || !restQ) continue;

    const rest = new THREE.Quaternion(restQ[0], restQ[1], restQ[2], restQ[3]);
    const current = new THREE.Quaternion(pose[0], pose[1], pose[2], pose[3]);
    // delta = inv(rest) * current
    const delta = rest.clone().invert().multiply(current);

    const axis = curlAxis(finger, side, j);
    const angle = extractAxisAngle(delta, axis);
    const maxAngle = angles[j];
    // For normal fingers (curl around -X), extracting around -X gives positive
    // angle during flexion.  For the thumb CMC (compound axis), the angle is
    // also positive during curl.  Either way t = angle / max.
    // Legacy -X fingers: extractAxisAngle is called on CURL_AXIS (negative),
    // so the returned angle is positive for flexion.
    const t = maxAngle > 1e-6 ? Math.max(0, Math.min(1, angle / maxAngle)) : 0;
    sum += t;
    count++;
  }
  return count > 0 ? sum / count : 0;
}

/**
 * Read the average curl (0–1) across all five fingers of one hand.
 */
function readFistCurl(
  side: "L" | "R",
  objectId: string,
  bonePoses: Map<string, [number, number, number, number]> | undefined,
): number {
  const ALL_FINGERS = ["thumb", "index", "middle", "ring", "pinky"];
  let sum = 0;
  for (const f of ALL_FINGERS) {
    sum += readCurl(f, side, objectId, bonePoses);
  }
  return sum / ALL_FINGERS.length;
}

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

const EMPTY_SET = new Set<string>();
const COLOR_JOINT_NORMAL = COLOR_WARNING;
const COLOR_JOINT_SELECTED = COLOR_HAND_JOINT_SELECTED;
const COLOR_JOINT_LOCKED = COLOR_TEXT_FAINT;
const COLOR_LINE = COLOR_HAND_LINE;
const JOINT_RADIUS = 6;

// ---------------------------------------------------------------------------
// HandSVG — one hand schematic
// ---------------------------------------------------------------------------

function HandSVG({ side }: { side: "L" | "R" }) {
  const fingers = side === "L" ? LEFT_FINGERS : RIGHT_FINGERS;
  const poseMode = useStore((s) => s.poseMode);
  const selectedBones = poseMode?.selectedBones ?? EMPTY_SET;
  const objectId = poseMode?.objectId ?? null;
  const lockedBonesSet = useStore((s) =>
    poseMode?.objectId ? s.lockedBones.get(poseMode.objectId) : undefined,
  );
  const lockedBones = lockedBonesSet ?? EMPTY_SET;
  const selectBone = useStore((s) => s.selectBone);

  const handleClick = useCallback(
    (name: string, e: React.MouseEvent) => {
      if (!objectId) return;
      if (e.shiftKey) {
        // Additive toggle
        selectBone(name, objectId, true);
      } else {
        // Exclusive select, or deselect if already the only selection
        selectBone(
          selectedBones.has(name) && selectedBones.size === 1 ? null : name,
          objectId,
        );
      }
    },
    [selectBone, selectedBones, objectId],
  );

  return (
    <svg
      width={120}
      height={200}
      viewBox="0 0 120 200"
      style={{ display: "block" }}
    >
      {/* Lines from wrist to base joints */}
      {fingers.map((f) => (
        <line
          key={`wrist-${f.name}`}
          x1={WRIST.x}
          y1={WRIST.y}
          x2={f.joints[0].x}
          y2={f.joints[0].y}
          stroke={COLOR_LINE}
          strokeWidth={1.5}
        />
      ))}

      {/* Lines between joints */}
      {fingers.map((f) =>
        f.joints
          .slice(0, -1)
          .map((j, i) => (
            <line
              key={`line-${f.name}-${i}`}
              x1={j.x}
              y1={j.y}
              x2={f.joints[i + 1].x}
              y2={f.joints[i + 1].y}
              stroke={COLOR_LINE}
              strokeWidth={1.5}
            />
          )),
      )}

      {/* Wrist circle (non-interactive) */}
      <circle
        cx={WRIST.x}
        cy={WRIST.y}
        r={4}
        fill="rgba(232, 160, 48, 0.3)"
        stroke={COLOR_LINE}
        strokeWidth={1}
      />

      {/* Joint circles */}
      {fingers.map((f) =>
        f.joints.map((j, i) => {
          const name = boneName(f.name, side, i + 1);
          const isSelected = selectedBones.has(name);
          const isLocked = lockedBones.has(name);

          let fill = COLOR_JOINT_NORMAL;
          if (isSelected) fill = COLOR_JOINT_SELECTED;
          else if (isLocked) fill = COLOR_JOINT_LOCKED;

          return (
            <g
              key={name}
              style={{ cursor: "pointer" }}
              onClick={(e) => handleClick(name, e)}
            >
              <circle
                cx={j.x}
                cy={j.y}
                r={JOINT_RADIUS}
                fill={fill}
                opacity={isSelected ? 1 : 0.85}
                stroke={isSelected ? COLOR_JOINT_SELECTED : "none"}
                strokeWidth={isSelected ? 2 : 0}
              >
                <title>{name}</title>
              </circle>
              {/* Locked indicator: x mark */}
              {isLocked && (
                <>
                  <line
                    x1={j.x - 3}
                    y1={j.y - 3}
                    x2={j.x + 3}
                    y2={j.y + 3}
                    stroke="#222"
                    strokeWidth={1.5}
                    pointerEvents="none"
                  />
                  <line
                    x1={j.x + 3}
                    y1={j.y - 3}
                    x2={j.x - 3}
                    y2={j.y + 3}
                    stroke="#222"
                    strokeWidth={1.5}
                    pointerEvents="none"
                  />
                </>
              )}
            </g>
          );
        }),
      )}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Finger curl slider — contextual, shown only when a finger bone is selected
// ---------------------------------------------------------------------------

const FINGER_NAMES_DISPLAY: Record<string, string> = {
  thumb: "Thumb",
  index: "Index",
  middle: "Middle",
  ring: "Ring",
  pinky: "Pinky",
};

/** Extract which finger (thumb/index/etc) and side from a bone name like finger_indexL2 */
function parseFingerBone(
  boneName: string,
): { finger: string; side: "L" | "R" } | null {
  for (const f of ["thumb", "index", "middle", "ring", "pinky"]) {
    if (boneName.includes(f)) {
      const side = boneName.includes("L") ? ("L" as const) : ("R" as const);
      return { finger: f, side };
    }
  }
  return null;
}

/** Detect whether a wrist bone (handL / handR) is selected, and which side. */
function parseWristBone(name: string): { side: "L" | "R" } | null {
  if (name === "handL") return { side: "L" };
  if (name === "handR") return { side: "R" };
  return null;
}

const ALL_FINGER_NAMES = ["thumb", "index", "middle", "ring", "pinky"] as const;

function FingerCurlSlider() {
  const poseMode = useStore((s) => s.poseMode);
  const objectId = poseMode?.objectId ?? null;
  const bonePoses = useStore((s) =>
    objectId ? s.mannequinBonePoses.get(objectId) : undefined,
  );
  const setBonePoses = useStore((s) => s.setBonePoses);
  const undo = useDeferredUndo();
  const [curlValue, setCurlValue] = useState(0);
  const lastKey = useRef<string | null>(null);

  // Determine mode: single finger, fist (wrist selected), or nothing
  const mode = (() => {
    if (!poseMode) return null;
    // Check active bone first for single-finger
    if (poseMode.activeBone) {
      const parsed = parseFingerBone(poseMode.activeBone);
      if (parsed) return { kind: "finger" as const, ...parsed };
      const wrist = parseWristBone(poseMode.activeBone);
      if (wrist) return { kind: "fist" as const, ...wrist };
    }
    // Check any selected bone
    for (const b of poseMode.selectedBones) {
      const parsed = parseFingerBone(b);
      if (parsed) return { kind: "finger" as const, ...parsed };
    }
    for (const b of poseMode.selectedBones) {
      const wrist = parseWristBone(b);
      if (wrist) return { kind: "fist" as const, ...wrist };
    }
    return null;
  })();

  // Sync slider value when switching to a different finger / fist
  const modeKey = mode
    ? mode.kind === "finger"
      ? `${mode.finger}${mode.side}`
      : `fist${mode.side}`
    : null;
  if (modeKey !== lastKey.current) {
    lastKey.current = modeKey;
    if (modeKey && objectId) {
      // Read current curl from stored bone poses
      let recovered: number;
      if (mode!.kind === "fist") {
        recovered = readFistCurl(mode!.side, objectId, bonePoses);
      } else {
        recovered = readCurl(
          (mode as any).finger,
          mode!.side,
          objectId,
          bonePoses,
        );
      }
      setCurlValue(Math.round(recovered * 100));
    }
  }

  if (!mode || !objectId) return null;

  const label =
    mode.kind === "finger"
      ? `${FINGER_NAMES_DISPLAY[mode.finger]} Curl`
      : "Fist";

  return (
    <div style={{ marginTop: 8 }} data-no-drag>
      <SliderRow
        label={label}
        value={curlValue}
        min={0}
        max={100}
        displayValue={`${curlValue}%`}
        labelWidth={70}
        onStart={undo.pushOnce}
        onEnd={undo.reset}
        onChange={(v) => {
          setCurlValue(v);
          const t = v / 100;

          if (mode.kind === "finger") {
            const poses = computeCurl(mode.finger, mode.side, t, objectId);
            if (poses.size > 0) setBonePoses(objectId, poses);
          } else {
            // Fist: curl all five fingers
            const allPoses = new Map<
              string,
              [number, number, number, number]
            >();
            for (const f of ALL_FINGER_NAMES) {
              for (const [k, q] of computeCurl(f, mode.side, t, objectId)) {
                allPoses.set(k, q);
              }
            }
            if (allPoses.size > 0) setBonePoses(objectId, allPoses);
          }
        }}
        dimChromeOnDrag
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// HandPosePanel
// ---------------------------------------------------------------------------

const GRIP_PILL_WIDTH = 28;
const GRIP_PILL_HEIGHT = 3;

/** Poll camera position and recompute hand order when it changes. */
function useHandOrder(objectId: string | null): ["L" | "R", "L" | "R"] {
  const [order, setOrder] = useState<["L" | "R", "L" | "R"]>(["L", "R"]);
  const lastCamPos = useRef("");

  // Only poll when the panel is visible (hand bone selected)
  const hasHandBone = useStore((s) => {
    if (!s.poseMode) return false;
    for (const b of s.poseMode.selectedBones) {
      if (b.startsWith("hand") || b.startsWith("finger_")) return true;
    }
    return false;
  });

  useEffect(() => {
    if (!objectId || !hasHandBone) return;
    const interval = setInterval(() => {
      const camera = window.__poseref_camera as
        | THREE.PerspectiveCamera
        | undefined;
      if (!camera) return;
      const key = `${camera.position.x.toFixed(1)},${camera.position.y.toFixed(1)},${camera.position.z.toFixed(1)}`;
      if (key === lastCamPos.current) return;
      lastCamPos.current = key;
      const newOrder = computeHandOrder(objectId);
      setOrder((prev) => (prev[0] === newOrder[0] ? prev : newOrder));
    }, 500);
    return () => clearInterval(interval);
  }, [objectId, hasHandBone]);

  return order;
}

function computeHandOrder(objectId: string | null): ["L" | "R", "L" | "R"] {
  if (!objectId) return ["L", "R"];

  const camera = window.__poseref_camera as THREE.PerspectiveCamera | undefined;
  if (!camera) return ["L", "R"];

  const leftWrist = getBone(objectId, "handL");
  const rightWrist = getBone(objectId, "handR");
  if (!leftWrist || !rightWrist) return ["L", "R"];

  const leftPos = new THREE.Vector3();
  const rightPos = new THREE.Vector3();
  leftWrist.updateWorldMatrix(true, false);
  rightWrist.updateWorldMatrix(true, false);
  leftWrist.getWorldPosition(leftPos);
  rightWrist.getWorldPosition(rightPos);

  const leftSS = leftPos.project(camera);
  const rightSS = rightPos.project(camera);

  // If wrists overlap in X (within 5%), fall back to model's natural orientation
  if (Math.abs(leftSS.x - rightSS.x) < 0.05) return ["L", "R"];

  // Place the hand that appears on the LEFT of the screen in the left column
  return leftSS.x < rightSS.x ? ["L", "R"] : ["R", "L"];
}

export function HandPosePanel() {
  const poseMode = useStore((s) => s.poseMode);
  const inPoseMode = poseMode != null;
  const [leftCol, rightCol] = useHandOrder(poseMode?.objectId ?? null);

  // Auto-show when a hand or finger bone is selected
  const hasHandBone = useStore((s) => {
    if (!s.poseMode) return false;
    for (const b of s.poseMode.selectedBones) {
      if (b.startsWith("hand") || b.startsWith("finger_")) return true;
    }
    return false;
  });

  if (!hasHandBone || !inPoseMode) return null;

  const leftLabel = leftCol === "L" ? "Left Hand" : "Right Hand";
  const rightLabel = rightCol === "L" ? "Left Hand" : "Right Hand";

  return (
    <DraggablePanel
      id="hand-pose-panel"
      defaultPosition={{ top: 120, right: 16 }}
      zIndex={Z_POPOVER}
      style={{
        ...GLASS_PANEL,
        borderRadius: RADIUS_PANEL,
        padding: "6px 12px 12px",
      }}
    >
      {/* Drag handle */}
      <div
        data-drag-handle
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "4px 0 6px",
          cursor: "grab",
          minHeight: 24,
        }}
      >
        <div
          style={{
            width: GRIP_PILL_WIDTH,
            height: GRIP_PILL_HEIGHT,
            borderRadius: GRIP_PILL_HEIGHT,
            background: "rgba(255,255,255,0.20)",
          }}
        />
      </div>

      {/* Title */}
      <div
        style={{
          textAlign: "center",
          fontSize: FONT_SIZE_SMALL,
          fontWeight: 500,
          color: COLOR_TEXT_SECONDARY,
          marginBottom: 8,
        }}
      >
        Hand Pose
      </div>

      {/* Two columns — ordered to match camera view */}
      <div style={{ display: "flex", gap: 12 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: FONT_SIZE_TINY,
              color: COLOR_TEXT_SECONDARY,
              marginBottom: 4,
            }}
          >
            {leftLabel}
          </span>
          <HandSVG side={leftCol} />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: FONT_SIZE_TINY,
              color: COLOR_TEXT_SECONDARY,
              marginBottom: 4,
            }}
          >
            {rightLabel}
          </span>
          <HandSVG side={rightCol} />
        </div>
      </div>

      {/* Contextual curl slider — appears when a finger bone is selected */}
      <FingerCurlSlider />
    </DraggablePanel>
  );
}
